import { useEffect, useState } from 'react';

import { API_BASE_URL, fetchReady } from '../lib/api';
import type { ReadyResponse } from '@nexus/shared';
import { Card, Skeleton } from '../ui';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; body: ReadyResponse }
  | { kind: 'error'; message: string };

/**
 * Whether the API is answering, and how fast its database is.
 *
 * This lives in the OPERATOR area, not on a creator's home page: "d1, ok,
 * 3 ms" is a sentence for the person who would do something about it. A
 * creator who cannot load their wallet is told so by the wallet.
 */
export function ApiStatus(): React.JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchReady()
      .then((body) => {
        if (!cancelled) setState({ kind: 'ready', body });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card
      label="API"
      eyebrow="API"
      title={
        state.kind === 'ready' ? (
          <>
            Status: <span className={state.body.status === 'ok' ? 'tone-positive' : 'tone-critical'}>{state.body.status}</span>
          </>
        ) : state.kind === 'error' ? (
          'Not answering'
        ) : (
          'Checking…'
        )
      }
      sub={`${API_BASE_URL}/ready`}
    >
      {state.kind === 'loading' && <Skeleton lines={2} />}

      {state.kind === 'error' && (
        <p role="alert" className="alert" data-tone="critical">
          Could not reach the API: {state.message}
        </p>
      )}

      {state.kind === 'ready' && (
        <ul className="checklist">
          {Object.entries(state.body.checks).map(([name, check]) => (
            <li key={name} data-state={check.ok ? 'pass' : 'fail'}>
              <span className="mark" aria-hidden="true">
                {check.ok ? '✓' : '✕'}
              </span>
              <span>
                <span className="check-label">{name}</span>{' '}
                <span className={check.ok ? 'muted' : ''}>{check.ok ? 'ok' : 'failing'}</span>{' '}
                <span className="muted num">{check.latencyMs} ms</span>
                {check.error && <div className="check-note">{check.error}</div>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
